<script setup>
// IMPORTS
import { computed, onMounted, ref } from "vue";

// DEPENDENCIES
const { mode } = useApp();
const { can } = usePermissions();
const { layoutConfig, setActiveMenuItem } = useLayout();

// INJECT

// PROPS, EMITS

// REFS

// COMPUTED
const navItems = ref([
  {
    label: "Flows",
    routeName: "flows",
    svgPath: new URL("/icons/nav/flows.svg", import.meta.url).href,
    visibility: can("flows.read"),
    svgContent: "",
    children: [],
  },
  {
    label: "Integrations",
    routeName: "integrations",
    svgPath: new URL("/icons/nav/integrations.svg", import.meta.url).href,
    visibility: can("integrations.read"),
    svgContent: "",
    children: [
      {
        label: "APIs",
        routeName: "listApis",
        svgPath: new URL("/icons/nav/api.svg", import.meta.url).href,
        visibility: true,
        svgContent: "",
      },
      {
        label: "Web Services",
        routeName: "listWebServices",
        svgPath: new URL("/icons/nav/ws.svg", import.meta.url).href,
        visibility: true,
        svgContent: "",
      },
    ],
  },
  {
    label: "Customization",
    routeName: "customization",
    svgPath: new URL("/icons/nav/customization.svg", import.meta.url).href,
    visibility: can("customizations.read"),
    svgContent: "",
    children: [],
  },
  {
    label: "QA Tools",
    routeName: "elementInspector",
    svgPath: new URL("/icons/nav/qa-tools.svg", import.meta.url).href,
    visibility: can("qatools.read"),
    svgContent: "",
    children: [
      {
        label: "Element inspector",
        routeName: "elementInspector",
        svgPath: new URL("/icons/nav/element-inspector.svg", import.meta.url).href,
        visibility: true,
        svgContent: "",
      },
      {
        label: "Session viewer",
        routeName: "sessionViewer",
        svgPath: new URL("/icons/nav/session-viewer.svg", import.meta.url).href,
        visibility: true,
        svgContent: "",
      },
      {
        label: "Integration monitor",
        routeName: "integrationMonitor",
        svgPath: new URL("/icons/nav/integration-monitor.svg", import.meta.url).href,
        visibility: true,
        svgContent: "",
      },
    ],
  },
  {
    label: "Users",
    routeName: "users",
    svgPath: new URL("/icons/nav/users.svg", import.meta.url).href,
    visibility: can("users.read"),
    svgContent: "",
    children: [],
  },
  {
    label: "Organizations",
    routeName: "organizations",
    svgPath: new URL("/icons/nav/users.svg", import.meta.url).href,
    visibility: can("organizations.read"),
    svgContent: "",
    children: [],
  },
  {
    label: "Plans",
    routeName: "plans",
    svgPath: new URL("/icons/nav/users.svg", import.meta.url).href,
    visibility: can("organizations.read"),
    svgContent: "",
    children: [],
  },
]);

const navClasses = computed(() => {
  return {
    "is-editor": mode.value === "editor",
  };
});

// COMPONENT METHODS AND LOGIC
const fetchSvgContent = async (navItem) => {
  const response = await fetch(navItem.svgPath);
  const svgContent = await response.text();
  navItem.svgContent = svgContent;
};

// Generate item key for parent nav items
const getItemKey = (index) => {
  return String(index);
};

// Generate item key for child nav items
const getChildKey = (parentIndex, childIndex) => {
  return `${parentIndex}-${childIndex}`;
};

// Check if a parent nav item is active
const isNavItemActive = (index) => {
  const itemKey = getItemKey(index);
  const activeKey = layoutConfig.activeMenuItem.value;
  if (!activeKey) return false;
  return activeKey === itemKey || activeKey.startsWith(itemKey + "-");
};

// Check if a child nav item is active
const isChildActive = (parentIndex, childIndex) => {
  const childKey = getChildKey(parentIndex, childIndex);
  const activeKey = layoutConfig.activeMenuItem.value;
  if (!activeKey) return false;
  return activeKey === childKey;
};

// Handle parent nav item click
const handleNavItemClick = (index) => {
  const itemKey = getItemKey(index);
  setActiveMenuItem(itemKey);
};

// Handle child nav item click
const handleChildClick = (parentIndex, childIndex) => {
  const childKey = getChildKey(parentIndex, childIndex);
  setActiveMenuItem(childKey);
};

// PROVIDE, EXPOSE

// WATCHERS

// LIFECYCLE METHODS (https://vuejs.org/api/composition-api-lifecycle.html)
onMounted(() => {
  navItems.value.forEach((navItem) => {
    fetchSvgContent(navItem);
    // Load SVG content for children
    if (navItem.children && navItem.children.length > 0) {
      navItem.children.forEach((child) => {
        fetchSvgContent(child);
      });
    }
  });
});
</script>

<template>
  <div
    class="main-nav flex flex-col items-center pt-5 pr-2"
    :class="navClasses"
  >
    <template
      v-for="(navItem, index) in navItems"
      :key="navItem.label"
    >
      <div
        v-if="navItem.visibility"
        class="nav-item-wrapper"
        :class="{ 'has-children': navItem.children && navItem.children.length > 0 }"
      >
        <router-link
          :to="{ name: navItem.routeName }"
          class="nav-item align-center flex w-full gap-2"
          :class="{ 'active-menuitem': isNavItemActive(index) }"
          @click="handleNavItemClick(index)"
        >
          <div v-html="navItem.svgContent"></div>
          <span class="heading-xxs font-light">{{ navItem.label }}</span>
        </router-link>

        <!-- Child items -->
        <div
          v-if="navItem.children && navItem.children.length > 0"
          class="nav-children"
        >
          <router-link
            v-for="(child, childIndex) in navItem.children"
            :key="child.label"
            :to="{ name: child.routeName }"
            class="nav-child-item"
            :class="{ 'active-menuitem': isChildActive(index, childIndex) }"
            v-show="child.visibility"
            @click="handleChildClick(index, childIndex)"
          >
            <div v-html="child.svgContent"></div>
            <span class="heading-xxs font-light">{{ child.label }}</span>
          </router-link>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.main-nav {
  background-color: var(--color-dark-gray-1950);
  height: 100%;
  box-shadow: 0px 8px 20px 0px #000000;

  .nav-item-wrapper {
    position: relative;
    width: 100%;
    margin-bottom: 0.5rem;

    &.has-children {
      .nav-children {
        display: none;
      }

      &:hover .nav-children,
      .nav-children:hover,
      &:hover::after {
        display: block;
      }

      // Safe area bridge between parent and children
      &::after {
        content: "";
        position: absolute;
        left: 100%;
        top: 0;
        width: 16px;
        height: 100%;
        display: none;
      }
    }
  }

  .nav-item {
    color: var(--color-gray-300);
    border-width: 0.5px 0.5px 0.5px 0px;
    border-color: transparent;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 8px 24px 8px 24px;
    gap: 8px;
    border-radius: 0px 100px 100px 0px;
    transition: background-color 0.3s;
    font-weight: 400 !important;

    :deep(svg *) {
      stroke: var(--color-gray-300);
    }

    &:hover {
      background-color: var(--color-dark-gray-2050);
      color: var(--color-white);

      :deep(svg *) {
        stroke: var(--color-white);
        stroke-width: 2;
      }
    }

    &.active-menuitem {
      background-color: var(--color-dark-gray-2050);
      color: var(--color-white);
      border-color: var(--color-gray-600);
      border-width: 0.5px 0.5px 0.5px 0px;

      :deep(svg *) {
        stroke: var(--color-white);
        stroke-width: 2;
      }
    }
  }

  .nav-children {
    position: absolute;
    left: calc(100% + 16px);
    top: 0;
    background-color: var(--color-dark-gray-1950);
    border: 0.5px solid var(--color-gray-600);
    border-radius: 8px;
    padding: 8px;
    min-width: 200px;
    box-shadow: 4px 4px 12px 0px rgba(0, 0, 0, 0.3);
    z-index: 1000;
  }

  .nav-child-item {
    color: var(--color-gray-300);
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 4px;
    transition: background-color 0.3s;
    font-weight: 400 !important;
    white-space: nowrap;

    :deep(svg) {
      width: 20px;
      height: 20px;
    }

    :deep(svg *) {
      stroke: var(--color-gray-300);
    }

    &:hover {
      background-color: var(--color-dark-gray-2050);
      color: var(--color-white);

      :deep(svg *) {
        stroke: var(--color-white);
        stroke-width: 2;
      }
    }

    &.active-menuitem {
      background-color: var(--color-dark-gray-2050);
      color: var(--color-white);

      :deep(svg *) {
        stroke: var(--color-white);
        stroke-width: 2;
      }
    }
  }

  // &.is-editor {
  //   background-color: var(--color-dark-gray-1950);

  //   .nav-item {
  //     color: var(--color-gray-300);

  //     :deep(svg *) {
  //       stroke: var(--stroke-gray-300);
  //     }

  //     &:hover {
  //       color: var(--color-white);

  //       :deep(svg *) {
  //         stroke: var(--color-white);
  //       }
  //     }
  //   }
  // }
}
</style>
